import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, X } from 'lucide-react';

import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { useHaptics } from '@/hooks/useHaptics';

interface TournamentPasswordSheetProps {
  /** Render the sheet when true (parent mounts it only while opening). */
  open: boolean;
  /** Disables submit while the register request is in flight. */
  busy: boolean;
  /** Error message shown in the sheet (e.g. wrong password). */
  error?: string | null;
  onSubmit: (secretCode: string) => void;
  onClose: () => void;
}

/**
 * Bottom sheet that prompts for a private tournament's password (secret code)
 * before registering. Mirrors the mobile-app `insert-password.tsx` flow:
 * the register button is disabled until a non-empty code is entered, and the
 * parent passes the registration error back so a wrong password is surfaced.
 */
export function TournamentPasswordSheet({
  open,
  busy,
  error,
  onSubmit,
  onClose,
}: TournamentPasswordSheetProps) {
  useLivelloIndietro(open, onClose);
  const { t } = useTranslation('tournaments');
  const haptics = useHaptics();
  const [code, setCode] = useState('');

  if (!open) return null;

  const valid = code.trim().length > 0;
  const submit = () => {
    if (!valid || busy) return;
    haptics.impact('medium');
    onSubmit(code.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('cancel', { defaultValue: 'Cancel' })}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Lock aria-hidden className="h-4 w-4 text-brand" />
            {t('tournamentPassword', { defaultValue: 'Tournament password' })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('cancel', { defaultValue: 'Cancel' })}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted active:opacity-80"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </div>

        <input
          autoFocus
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('passwordDots', { defaultValue: 'Password…' })}
          className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
        />

        {error && (
          <p className="mt-2 rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!valid || busy}
          className="mt-4 flex w-full items-center justify-center rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-[opacity,background-color,color] active:opacity-80 disabled:bg-bg-tertiary disabled:text-fg-muted disabled:opacity-100"
        >
          {busy
            ? t('registering', { defaultValue: 'Registering…' })
            : t('register', { defaultValue: 'Register' })}
        </button>
      </div>
    </div>
  );
}
